import { forwardRef } from "react";
import moment from "moment";
import { SELLER, amountInWords } from "../../../utils/staticData";
import {
  ApiInvoice, customerName, inr2, invoiceLine, invoiceNo, invoiceTotals, isWalletInvoice, planTypeInfo,
} from "../invoiceUtils";

// Printable GST tax invoice (same layout on screen and in the PDF)
const InvoiceDocument = forwardRef<HTMLDivElement, { invoice: ApiInvoice }>(({ invoice: i }, ref) => {
  const t = invoiceTotals(i);
  const paid = i.invoice_status === 1;
  const user = i.invoice_user_id;
  const addr = [i.invoice_bill_building_name, i.invoice_bill_street_name, i.invoice_bill_city, i.invoice_bill_postal_code].filter(Boolean).join(", ");
  const state = [i.invoice_bill_state, i.invoice_bill_country].filter(Boolean).join(", ");
  const discount = Number(i.invoice_custom_discount || 0);
  const credit = Number(i.invoice_credit_applied || 0);

  return (
    <div className="cx-invd" ref={ref}>
      <div className="cx-invd-top">
        <div>
          <img src="/uomox-logo.png" alt="UOMOX" />
          <div className="cx-invd-s">
            {SELLER.name}<br />{SELLER.addr}<br />
            GSTIN <b>{SELLER.gstin}</b> · PAN {SELLER.pan}<br />{SELLER.email}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div className="cx-invd-h">TAX INVOICE</div>
          <div className="cx-invd-s">Original for recipient</div>
          <div className={`cx-invd-st ${paid ? "paid" : "pending"}`}>{paid ? "PAID" : "UNPAID"}</div>
        </div>
      </div>

      <div className="cx-invd-meta">
        <div>
          <small>Bill to</small>
          <b>{i.invoice_bill_buisness_name || customerName(i)}</b>
          <span>{[i.invoice_bill_name, i.invoice_bill_email || user?.user_email, i.invoice_bill_contact_no].filter(Boolean).join(" · ")}</span>
          {addr && <span>{addr}</span>}
          <span>{state || "—"}{i.invoice_bill_gst_no ? <> · GSTIN <b className="mono">{i.invoice_bill_gst_no}</b></> : " · Unregistered"}</span>
        </div>
        <div className="cx-invd-kv">
          <span>Invoice no.</span><b className="mono">{invoiceNo(i)}</b>
          <span>Invoice date</span><b>{i.createdAt ? moment(i.createdAt).format("DD MMM YYYY") : "—"}</b>
          <span>Type</span><b>{planTypeInfo(i.invoice_plan_type)[0]}</b>
          <span>Place of supply</span><b>{i.invoice_bill_state || "—"}</b>
          <span>Workspace</span><b className="mono">{user?.user_unique_id || "—"}</b>
        </div>
      </div>

      <table className="cx-invd-t">
        <thead>
          <tr><th>#</th><th>Description</th><th>SAC</th><th className="r">Qty</th><th className="r">Rate</th><th className="r">Taxable</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>
              {invoiceLine(i)}
              {i.invoice_description && !isWalletInvoice(i) && <div className="fs12 muted">{i.invoice_description}</div>}
            </td>
            <td className="mono">{SELLER.sac}</td>
            <td className="r">1</td>
            <td className="mono r">{inr2(t.sub)}</td>
            <td className="mono r">{inr2(t.sub)}</td>
          </tr>
        </tbody>
      </table>

      <div className="cx-invd-foot">
        <div className="cx-invd-s" style={{ maxWidth: "52%" }}>
          <b style={{ color: "#12131c" }}>Amount in words</b><br />
          Rupees {amountInWords(t.tot)} Only<br /><br />
          <b style={{ color: "#12131c" }}>Payment</b><br />
          {i.invoice_transaction_id?.method ? i.invoice_transaction_id.method.toUpperCase() : "—"}
          {i.invoice_transaction_id?.transaction_order_id && <> · <span className="mono">{i.invoice_transaction_id.transaction_order_id}</span></>}
          {i.invoice_plan_type === "wallet_topup" && <>
            <br /><br /><b style={{ color: "#12131c" }}>Wallet</b><br />
            {inr2(t.sub)} credited to wallet (taxable value). GST charged on recharge.
          </>}
        </div>
        <div className="cx-invd-tot">
          <span>Taxable value</span><b>{inr2(t.sub)}</b>
          {discount > 0 && <><span>Discount</span><b>−{inr2(discount)}</b></>}
          {credit > 0 && <><span>Credit applied</span><b>−{inr2(credit)}</b></>}
          {t.intra ? <>
            <span>CGST 9%</span><b>{inr2(t.cgst)}</b>
            <span>SGST 9%</span><b>{inr2(t.sgst)}</b>
          </> : <>
            <span>IGST 18%</span><b>{inr2(t.igst)}</b>
          </>}
          <span className="gt">Total</span><b className="gt">{inr2(t.tot)}</b>
        </div>
      </div>

      <div className="cx-invd-note">{SELLER.brand} is a brand of {SELLER.name}. This is a computer generated invoice and does not require a signature. Reverse charge: No.</div>
    </div>
  );
});

export default InvoiceDocument;
